/**
 * Client-side closing-package zip assembly (T129-012).
 *
 * Assembles a zip file containing:
 *   1. invoices.csv         — fetched from GET /api/invoices.csv (format-negotiated index)
 *   2. payments.csv         — fetched from GET /api/payments.csv
 *   3. expenses.csv         — fetched from GET /api/expenses.csv
 *   4. bank_reconciliation.csv — rendered client-side from bank statement JSON
 *      (no new endpoint; uses the existing /api/bank_statements response already
 *      loaded on the page; silently omitted if unavailable)
 *   5. journal_entries.csv  — rendered client-side from /api/journal_entries JSON;
 *      omitted silently when accounting is not enabled (noted in manifest.txt)
 *   6. wht_certificates/wht-<id>.pdf — generated via whtCertificatePdfGenerator
 *      using ONLY manifest-embedded data (zero extra fetches per T129-006 correction)
 *   7. evidence/expense-<id>/<filename> — from presigned URLs in the manifest
 *   8. manifest.txt         — period, record counts, completeness warnings
 *
 * All logic is encapsulated in `assembleClosingPackage`. The caller passes the
 * manifest returned by `useClosingPackage`, a template fetch function (one fetch
 * for the WHT template, shared across all certificates), and optional CSV strings
 * if the caller has them pre-loaded (for bank reconciliation / journal entries).
 */

import { strToU8, zip as fflateZip } from 'fflate'
import { api } from '@/lib/api'
import { generateWhtCertificatePdf } from '@/lib/whtCertificatePdfGenerator'
import type { Template } from '@pdfme/common'
import type { ClosingPackageManifest, ClosingPackageWhtExpense } from '@/hooks/useClosingPackage'
import i18n from '@/i18n'
import type { DateFormatOptions } from '@/lib/date'
import { closingPackageCoverageWarning } from '@/lib/closingPackageCoverage'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ClosingPackageOptions {
  /** YYYY-MM month string for the export period. */
  month: string
  /** Window boundaries (ISO date strings) for CSV filter params. */
  windowFrom: string
  windowTo: string
  /** The manifest from useClosingPackage, already loaded. */
  manifest: ClosingPackageManifest
  /** Same language and account date configuration as the checklist. */
  locale?: string
  dateOptions?: DateFormatOptions
  /**
   * Whether the accounting module is enabled.
   * When false, journal_entries.csv is silently omitted.
   */
  accountingEnabled?: boolean
  /**
   * Optional pre-loaded bank-reconciliation CSV text rendered client-side.
   * Pass undefined to omit the file (no new endpoint required).
   */
  bankReconciliationCsv?: string | undefined
  /**
   * Optional pre-loaded journal-entries CSV text rendered client-side.
   * Omitted from zip when accounting is disabled or string is not provided.
   */
  journalEntriesCsv?: string | undefined
  /**
   * Callback to fetch the WHT document template (called once, shared).
   * Returns the parsed pdfme Template. Pass undefined to skip WHT generation.
   */
  fetchWhtTemplate?: () => Promise<Template>
}

export interface ClosingPackageResult {
  blob: Blob
  filename: string
}

// ---------------------------------------------------------------------------
// CSV fetchers
// ---------------------------------------------------------------------------

async function fetchCsv(url: string, params: Record<string, string>): Promise<string> {
  const { data } = await api.get<string>(url, {
    params,
    responseType: 'text',
  })
  return data
}

// ---------------------------------------------------------------------------
// WHT certificate generation
//
// Uses ONLY manifest-embedded data (T129-006 correction: zero extra fetches).
// Builds minimal Expense/Vendor/Property shapes from the embedded wht_expense
// data so the existing generateWhtCertificatePdf function works unchanged.
// ---------------------------------------------------------------------------

async function generateWhtPdf(
  whtExpense: ClosingPackageWhtExpense,
  template: Template,
): Promise<Uint8Array> {
  // Build minimal Expense/Vendor/Property objects from the embedded manifest data.
  // The generator only needs these fields; we do NOT call any API endpoints.
  const expense = {
    id: whtExpense.id,
    expense_date: whtExpense.expense_date,
    description: whtExpense.description ?? '',
    // The generator accesses: total_amount, wht_amount, wht_rate, net_cash_amount
    amount: whtExpense.amount,
    total_amount: whtExpense.amount,
    wht_rate: whtExpense.wht_rate,
    wht_amount: whtExpense.wht_amount,
    net_cash_amount: whtExpense.amount - whtExpense.wht_amount,
    category: null,
    vendor_name: whtExpense.vendor_name ?? '',
    vendor_name_resolved: whtExpense.vendor_name ?? '',
  }

  const vendor = whtExpense.vendor_name != null
    ? {
        name: whtExpense.vendor_name,
        tax_id: whtExpense.vendor_tax_id ?? undefined,
        address: whtExpense.vendor_address ?? undefined,
      }
    : null

  const property = {
    name: whtExpense.property_name ?? '',
    address: whtExpense.property_address ?? undefined,
    tax_id: undefined,
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return generateWhtCertificatePdf(expense as any, vendor as any, property as any, template)
}

// ---------------------------------------------------------------------------
// Evidence file download
// ---------------------------------------------------------------------------

async function fetchEvidenceFile(url: string): Promise<Uint8Array> {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Failed to fetch evidence: ${response.status}`)
  }
  const buffer = await response.arrayBuffer()
  return new Uint8Array(buffer)
}

// ---------------------------------------------------------------------------
// Manifest text generation
// ---------------------------------------------------------------------------

function buildManifestText(
  manifest: ClosingPackageManifest,
  accountingEnabled: boolean,
  warnings: string[],
  bankCoverageLine: string | null,
): string {
  const lines: string[] = [
    `FlowRent Closing Package`,
    `Period: ${manifest.month}`,
    `Window: ${manifest.window_from} to ${manifest.window_to}`,
    ``,
    `--- Record Counts ---`,
    `Invoices:   ${manifest.invoices_count}`,
    `Payments:   ${manifest.payments_count}`,
    `Expenses:   ${manifest.expenses_count}`,
    ``,
    `--- Completeness ---`,
    `Expenses missing receipt: ${manifest.expenses_missing_receipt_count}`,
    `Expenses with WHT:        ${manifest.expenses_wht_count}`,
    bankCoverageLine ?? `Unmatched bank rows:      ${manifest.bank_rows_unmatched_count}`,
    `Payments pending verify:  ${manifest.payments_pending_verification_count}`,
    `Payments unidentified:    ${manifest.payments_unidentified_count}`,
    ``,
  ]

  if (!accountingEnabled) {
    lines.push(`Note: journal_entries.csv omitted — accounting module not enabled.`)
    lines.push(``)
  }

  if (warnings.length > 0) {
    lines.push(`--- Warnings ---`)
    warnings.forEach((w) => lines.push(`* ${w}`))
    lines.push(``)
  }

  lines.push(`Generated: ${new Date().toISOString()}`)
  return lines.join('\n')
}

// ---------------------------------------------------------------------------
// Client-rendered CSVs (no export endpoints exist for these two — the JSON
// list endpoints are paged and rendered here; doc D-9 requires both files)
// ---------------------------------------------------------------------------

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

async function fetchAllPages<T>(url: string, params: Record<string, string>): Promise<T[]> {
  const rows: T[] = []
  let page = 1
  let totalPages = 1
  do {
    const { data } = await api.get<{ data: T[]; meta?: { total_pages?: number } }>(url, {
      params: { ...params, page: String(page) },
    })
    rows.push(...data.data)
    totalPages = data.meta?.total_pages ?? 1
    page += 1
  } while (page <= totalPages)
  return rows
}

interface BankTxnRow {
  transaction_date: string
  direction: 'credit' | 'debit'
  amount: number
  reference: string | null
  description: string | null
  match_status: string
  matchable_type: string | null
  matchable_id: number | null
}

export async function buildBankReconciliationCsv(month: string): Promise<string> {
  // Summary (direction-split) + full month rows, per doc D-9.
  const { data } = await api.get<{
    summary?: {
      credits?: { matched: number; unmatched: number; match_rate_pct: number }
      debits?: { matched: number; unmatched: number; match_rate_pct: number }
    }
  }>('/api/bank_transactions', { params: { month, page: '1' } })
  const rows = await fetchAllPages<BankTxnRow>('/api/bank_transactions', { month })

  const lines: string[] = []
  const s = data.summary
  lines.push('section,metric,value')
  if (s?.credits) {
    lines.push(`summary,credits_matched,${s.credits.matched}`)
    lines.push(`summary,credits_unmatched,${s.credits.unmatched}`)
    lines.push(`summary,credits_match_rate_pct,${s.credits.match_rate_pct}`)
  }
  if (s?.debits) {
    lines.push(`summary,debits_matched,${s.debits.matched}`)
    lines.push(`summary,debits_unmatched,${s.debits.unmatched}`)
    lines.push(`summary,debits_match_rate_pct,${s.debits.match_rate_pct}`)
  }
  lines.push('')
  lines.push('transaction_date,direction,amount,reference,description,match_status,matched_type,matched_id')
  for (const r of rows) {
    lines.push(
      [r.transaction_date, r.direction, r.amount, r.reference, r.description, r.match_status, r.matchable_type, r.matchable_id]
        .map(csvCell)
        .join(','),
    )
  }
  return lines.join('\n')
}

interface JournalEntryRow {
  id: number
  entry_number?: string
  entry_date: string
  description: string | null
  status: string
  total_debit?: number
  total_credit?: number
}

export async function buildJournalEntriesCsv(windowFrom: string, windowTo: string): Promise<string> {
  const rows = await fetchAllPages<JournalEntryRow>('/api/journal_entries', {
    from: windowFrom,
    to: windowTo,
  })
  const lines = ['entry_number,entry_date,description,status,total_debit,total_credit']
  for (const r of rows) {
    lines.push(
      [r.entry_number ?? r.id, r.entry_date, r.description, r.status, r.total_debit, r.total_credit]
        .map(csvCell)
        .join(','),
    )
  }
  return lines.join('\n')
}

// ---------------------------------------------------------------------------
// Main assembly function
// ---------------------------------------------------------------------------

export async function assembleClosingPackage(
  opts: ClosingPackageOptions,
): Promise<ClosingPackageResult> {
  const {
    month,
    windowFrom,
    windowTo,
    manifest,
    locale = i18n.language,
    dateOptions = {},
    accountingEnabled = false,
    bankReconciliationCsv,
    journalEntriesCsv,
    fetchWhtTemplate,
  } = opts

  const csvParams = { from: windowFrom, to: windowTo }
  // MON-10 (T149-123): invoices are selected by the month they bill, not the
  // day they were issued, matching the manifest's invoices_count. Payments
  // and expenses keep the date window.
  const invoiceCsvParams = { billing_period: month }
  const warnings: string[] = []
  const t = i18n.getFixedT(locale, 'common')
  const bankCoverageWarning = closingPackageCoverageWarning(manifest, t, dateOptions)

  // ── 1–3. Three primary CSVs (parallel fetch) ──────────────────────────────
  const [invoicesCsv, paymentsCsv, expensesCsv] = await Promise.all([
    fetchCsv('/api/invoices.csv', invoiceCsvParams),
    fetchCsv('/api/payments.csv', csvParams),
    fetchCsv('/api/expenses.csv', csvParams),
  ])

  // ── Completeness warnings ──────────────────────────────────────────────────
  if (bankCoverageWarning) warnings.push(bankCoverageWarning)
  if (manifest.expenses_missing_receipt_count > 0) {
    warnings.push(`${manifest.expenses_missing_receipt_count} expense(s) missing receipt`)
  }
  if (manifest.bank_rows_unmatched_count > 0) {
    warnings.push(`${manifest.bank_rows_unmatched_count} unmatched bank row(s)`)
  }
  if (manifest.payments_pending_verification_count > 0) {
    warnings.push(`${manifest.payments_pending_verification_count} payment(s) pending verification`)
  }
  if (manifest.payments_unidentified_count > 0) {
    warnings.push(`${manifest.payments_unidentified_count} unidentified payment(s)`)
  }

  // ── Zip file entries ───────────────────────────────────────────────────────
  const entries: Record<string, Uint8Array> = {}

  entries['invoices.csv'] = strToU8(invoicesCsv)
  entries['payments.csv'] = strToU8(paymentsCsv)
  entries['expenses.csv'] = strToU8(expensesCsv)

  // 4. Bank reconciliation CSV — caller-provided, else client-rendered from
  // the paged month JSON (doc D-9: the bundle always carries this file).
  try {
    const bankCsv = bankReconciliationCsv ?? (await buildBankReconciliationCsv(month))
    entries['bank_reconciliation.csv'] = strToU8(bankCsv)
  } catch (err) {
    warnings.push('bank_reconciliation.csv could not be generated')
    console.warn('[ClosingPackage] bank reconciliation CSV failed', err)
  }

  // 5. Journal entries CSV — accounting-gated; client-rendered from the paged
  // JSON when the module is enabled; silently omitted otherwise (noted here).
  if (accountingEnabled) {
    try {
      const jeCsv = journalEntriesCsv ?? (await buildJournalEntriesCsv(windowFrom, windowTo))
      entries['journal_entries.csv'] = strToU8(jeCsv)
    } catch (err) {
      warnings.push('journal_entries.csv could not be generated')
      console.warn('[ClosingPackage] journal entries CSV failed', err)
    }
  } else {
    warnings.push('journal_entries.csv omitted (accounting module disabled)')
  }

  // 6. WHT certificates — one per WHT expense; all data embedded in manifest
  if (manifest.wht_expenses.length > 0 && fetchWhtTemplate != null) {
    // One template fetch, shared across all certificates
    const whtTemplate = await fetchWhtTemplate()
    await Promise.all(
      manifest.wht_expenses.map(async (whtExpense) => {
        try {
          const pdfBytes = await generateWhtPdf(whtExpense, whtTemplate)
          entries[`wht_certificates/wht-${whtExpense.id}.pdf`] = pdfBytes
        } catch (err) {
          // Non-fatal — include a placeholder note; never block the download
          warnings.push(`WHT certificate for expense #${whtExpense.id} could not be generated`)
          console.warn('[ClosingPackage] WHT PDF generation failed', err)
        }
      }),
    )
  }

  // 7. Evidence files — presigned URLs from manifest (short-lived, download promptly)
  await Promise.all(
    manifest.evidence_urls.map(async (ev) => {
      try {
        const bytes = await fetchEvidenceFile(ev.url)
        // Derive a readable filename from the key (last segment after /)
        const filename = ev.key.split('/').pop() ?? ev.key
        entries[`evidence/expense-${ev.expense_id}/${filename}`] = bytes
      } catch (err) {
        warnings.push(`Evidence file for expense #${ev.expense_id} could not be fetched`)
        console.warn('[ClosingPackage] Evidence fetch failed', err)
      }
    }),
  )

  // 8. manifest.txt
  entries['manifest.txt'] = strToU8(buildManifestText(
    manifest, accountingEnabled, warnings,
    bankCoverageWarning ? `${t('closing_package.checklist_bank_statement')}: ${bankCoverageWarning}` : null,
  ))

  // ── Compress and return ────────────────────────────────────────────────────
  const zipBytes = await new Promise<Uint8Array>((resolve, reject) => {
    fflateZip(entries, { level: 6 }, (err, data) => {
      if (err) reject(err)
      else resolve(data)
    })
  })

  const blob = new Blob([zipBytes.buffer as ArrayBuffer], { type: 'application/zip' })
  const filename = `closing-package-${month}.zip`

  return { blob, filename }
}

// ---------------------------------------------------------------------------
// Download trigger
// ---------------------------------------------------------------------------

export function triggerZipDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
